<template>
  <v-input
    class="occurrence"
    :rules="rules"
    :value="value"
  >
    <v-input
      class="dateInput"
    >
      <v-select
        :items="dayOptions"
        :value="day"
        @input="day = $event; update()"
        label="Day"
        aria-label="Day"
        required
      />
      <v-select
        :items="monthOptions"
        :value="month"
        @input="month = $event; update()"
        label="Month"
        aria-label="Month"
        required
      />
      <v-select
        :items="yearOptions"
        :value="year"
        @input="year = $event; update()"
        label="Year"
        aria-label="Year"
        required
      />
      <v-btn
        alt="Delete"
        @click="$emit('delete')"
        class="occurrence__btn"
        color="secondary"
        text
        small
        v-if="deletable"
      >
        <v-icon>delete</v-icon>
      </v-btn>
    </v-input>
    <div class="times">
      <v-input
        class="timeInput"
      >
        <label class="timeInput__label">Start Time</label>
        <v-select
          :items="hourOptions"
          :value="startHour"
          @input="startHour = $event; update()"
          label="Hour"
          aria-label="Hour"
          required
        />
        <v-select
          :items="minuteOptions"
          :value="startMinute"
          @input="startMinute = $event; update()"
          label="Minute"
          aria-label="Minute"
          required
        />
        <v-select
          :items="amPmOptions"
          :value="startAmPm"
          @input="startAmPm = $event; update()"
          label="am/pm"
          aria-label="am/pm"
          required
        />
      </v-input>
      <v-input
        class="timeInput"
      >
        <label class="timeInput__label">End Time</label>
        <v-select
          :items="hourOptions"
          :value="endHour"
          @input="endHour = $event; update()"
          label="Hour"
          aria-label="Hour"
        />
        <v-select
          :items="minuteOptions"
          :value="endMinute"
          @input="endMinute = $event; update()"
          label="Minute"
          aria-label="Minute"
        />
        <v-select
          :items="amPmOptions"
          :value="endAmPm"
          @input="endAmPm = $event; update()"
          label="am/pm"
          aria-label="am/pm"
        />
        <v-btn
          alt="Clear end time"
          @click="clearEnd"
          class="occurrence__btn"
          color="secondary"
          text
          small
          v-show="endHasEntry()"
        >
          <v-icon>clear</v-icon>
        </v-btn>
      </v-input>
    </div>
  </v-input>
</template>

<script>
import { range, partial } from 'ramda';
import moment from 'moment';

const dateFormat = "Do MMMM YYYY h:mma";

const selectOptions = {
  dayOptions: range(1, 32).map(n => moment.utc(new Date(1, 0, n)).format('Do')),
  monthOptions: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  yearOptions: range(moment().year(), moment().year() + 5).map(String),
  hourOptions: range(1, 13).map(String),
  minuteOptions: range(0, 60).map(n => `00${n}`.slice(-2)),
  amPmOptions: ['am', 'pm'],
}

export default {
  name: 'occurrence',
  props: [
    'value',
    'deletable',
    'rules'
  ],
  data() {
    return {
      ...selectOptions,

      day: '',
      month: '',
      year: '',
      startHour: '',
      startMinute: '',
      startAmPm: '',
      endHour: '',
      endMinute: '',
      endAmPm: '',
    };
  },
  methods: {
    clearEnd() {
      this.endHour = '';
      this.endMinute = '';
      this.endAmPm = '';
      this.update();
    },
    endHasEntry() {
      return !!this.endHour || !!this.endMinute || !!this.endAmPm;
    },
    startString() {
      return `${this.dateString()} ${this.startHour}:${this.startMinute}${this.startAmPm}`;
    },
    endString() {
      return `${this.dateString()} ${this.endHour}:${this.endMinute}${this.endAmPm}`;
    },
    dateString() {
      return `${this.day} ${this.month} ${this.year}`;
    },
    getValue() {
      let value = {
        start: moment.utc(this.startString(), dateFormat),
        end: moment.utc(this.endString(), dateFormat),
      };
      value = {
        start: value.start.isValid() ? value.start : null,
        end: value.end.isValid() && this.endHasEntry() ? value.end : null,
      };
      return value;
    },
    update() {
      this.$emit('input', this.getValue());
    },
    resetValue(newValue, oldValue) {
      if (
        !!newValue &&
        !!newValue.start &&
        !!newValue.start.isValid &&
        !!newValue.start.isValid()
      ) {
        this.day = newValue.start.format('Do');
        this.month = newValue.start.format('MMMM');
        this.year = newValue.start.format('YYYY');
        this.startHour = newValue.start.format('h');
        this.startMinute = newValue.start.format('mm');
        this.startAmPm = newValue.start.format('a');
      } else if (
        !!oldValue &&
        !!oldValue.start &&
        !!oldValue.start.isValid &&
        !!oldValue.start.isValid()
      ) {
        this.day = '';
        this.month = '';
        this.year = '';
        this.startHour = '';
        this.startMinute = '';
        this.startAmPm = '';
      }

      if (
        !!newValue &&
        !!newValue.end &&
        !!newValue.end.isValid &&
        !!newValue.end.isValid()
      ) {
        this.endHour = newValue.end.format('h');
        this.endMinute = newValue.end.format('mm');
        this.endAmPm = newValue.end.format('a');
      }
    }
  },
  watch: {
    value(newValue, oldValue) {
      this.resetValue(newValue, oldValue);
    }
  },
  created() {
    this.resetValue(this.value, undefined);
  }
}
</script>

<style lang="scss">
.occurrence > .v-input__control > .v-input__slot {
  display: block !important;
}

.dateInput, .timeInput {
  & > .v-input__control > .v-input__slot {
    & > .v-input:nth-of-type(2) {
      margin-left: 0.4rem;
      margin-right: 0.4rem;
    }
  }
}

.occurrence__btn {
  min-width: 0 !important;
  padding: 0 $btn-icon-padding/2 !important;
  margin-left: 0.4rem;
}

.times {
  display: flex;
  flex-direction: column;
}

.dateInput, .times {
  .v-messages, .v-text-field__details {
    display: none;
  }
}

.occurrence {
  margin-bottom: 1rem;
}

.occurrence > .v-input__control > .v-messages {
  margin-top: -1rem;
}

.timeInput > .v-input__control > .v-input__slot {
  padding-top: 1rem;
}

.timeInput__label {
  position: absolute;
  top: 0;
  left: 0;
  font-size: 0.8em;
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
  .times {
    flex-direction: row;
  }

  .timeInput + .timeInput {
    margin-left: 1rem;
  }
}
</style>
